import React, { useCallback, useEffect, useState } from "react";
import { Badge, Button, ButtonGroup, Card, Form, Spinner, Table } from "react-bootstrap";
import moment from "moment";
import CommonLayout from "../../layouts/master/index";
import axiosApi from "../../../utilities/axiosApi";

const TRIGGER_LABEL = {
  admin: "an admin",
  schedule: "the hourly sync",
  console: "the command line",
};

const STATUS_LOOK = {
  draft: { bg: "secondary", label: "Draft" },
  pending: { bg: "warning", label: "Awaiting WhatsApp" },
  approved: { bg: "success", label: "Approved" },
  rejected: { bg: "danger", label: "Rejected" },
  paused: { bg: "warning", label: "Paused by WhatsApp" },
  disabled: { bg: "danger", label: "Disabled by WhatsApp" },
};

const ATTACHMENT_LABEL = {
  NONE: "None",
  TEXT: "Text heading",
  DOCUMENT: "PDF",
  IMAGE: "Image",
  VIDEO: "Video",
};

/**
 * "Sync from Meta": pull templates approved in Meta Business Manager into the
 * shared library every customer picks from.
 *
 * Always a preview first. Every template this creates is offered to every
 * customer, so the admin sees exactly what will change before anything does.
 * The same import also runs hourly on its own.
 */
export default function MetaTemplateSync() {
  const [lastRun, setLastRun] = useState(null);
  const [preview, setPreview] = useState(null);
  const [outcome, setOutcome] = useState(null);
  const [busy, setBusy] = useState(null); // "preview" | "import" | null
  const [notice, setNotice] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [owner, setOwner] = useState("all"); // "all" | "shared" | "customer"
  const [search, setSearch] = useState("");

  const loadTemplates = useCallback(() => {
    setLoadingList(true);

    axiosApi
      .get("/api/admin/meta-templates")
      .then((res) => setTemplates(res?.data?.success ? res.data.data || [] : []))
      .catch(() => setNotice({ type: "danger", text: "Could not load templates." }))
      .finally(() => setLoadingList(false));
  }, []);

  useEffect(() => {
    axiosApi
      .get("/api/admin/meta-templates/sync")
      .then((res) => {
        if (res?.data?.success) setLastRun(res.data.data.last_run);
      })
      .catch(() => {});

    loadTemplates();
  }, [loadTemplates]);

  const term = search.trim().toLowerCase();
  const visible = templates.filter((t) => {
    if (owner === "shared" && t.customer_id) return false;
    if (owner === "customer" && !t.customer_id) return false;
    if (!term) return true;

    return [t.display_name, t.name, t.body_text, t.customer?.organization_name]
      .some((v) => (v || "").toLowerCase().includes(term));
  });
  const sharedCount = templates.filter((t) => !t.customer_id).length;

  const run = (dryRun) => {
    setBusy(dryRun ? "preview" : "import");
    setNotice(null);

    axiosApi
      .post("/api/admin/meta-templates/sync", { dry_run: dryRun })
      .then((res) => {
        const data = res?.data?.data;

        if (!res?.data?.success) {
          setNotice({ type: "danger", text: res?.data?.message || "The sync failed." });
          return;
        }

        if (dryRun) {
          setPreview(data);
          setOutcome(null);
        } else {
          setOutcome(data);
          setPreview(null);
          setLastRun(data.last_run);
          setNotice({ type: "success", text: res.data.message });
          loadTemplates();
        }
      })
      .catch(() => setNotice({ type: "danger", text: "Could not reach the server." }))
      .finally(() => setBusy(null));
  };

  const shown = outcome || preview;

  return (
    <CommonLayout>
      <div className="p-4">
        <div className="d-flex justify-content-between align-items-start mb-3 gap-3 flex-wrap">
          <div>
            <h4 className="mb-1">WhatsApp Templates</h4>
            <p className="text-muted mb-0">
              Bring templates approved in Meta Business Manager into the shared
              library. Every customer can pick a shared template.
            </p>
            <p className="text-muted small mb-0 mt-1">
              {lastRun
                ? `Last synced ${moment(lastRun.at).fromNow()} by ${
                    TRIGGER_LABEL[lastRun.trigger] || lastRun.trigger
                  }${
                    lastRun.ok
                      ? ` — ${lastRun.created} new, ${lastRun.updated} updated, ${lastRun.skipped} skipped.`
                      : ` — failed: ${lastRun.error}`
                  }`
                : "Not synced yet."}{" "}
              It also runs on its own every hour.
            </p>
          </div>
          <Button onClick={() => run(true)} disabled={!!busy}>
            {busy === "preview" ? <Spinner size="sm" /> : "Sync from Meta"}
          </Button>
        </div>

        {notice && (
          <div className={`alert alert-${notice.type}`} role="alert">
            {notice.text}
          </div>
        )}

        {shown && (
          <Card className="mb-3">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3 gap-3 flex-wrap">
                <div>
                  <h6 className="mb-1">
                    {preview ? "Preview — nothing has changed yet" : "Imported"}
                  </h6>
                  <div className="small text-muted">
                    <Badge bg="success" className="me-1">{shown.created.length} new</Badge>
                    <Badge bg="secondary" className="me-1">{shown.updated.length} already known</Badge>
                    <Badge bg="warning" text="dark">{shown.skipped.length} skipped</Badge>
                  </div>
                </div>
                {preview && (
                  <div className="d-flex gap-2">
                    <Button variant="outline-secondary" onClick={() => setPreview(null)} disabled={!!busy}>
                      Cancel
                    </Button>
                    <Button onClick={() => run(false)} disabled={!!busy}>
                      {busy === "import" ? <Spinner size="sm" /> : "Import"}
                    </Button>
                  </div>
                )}
              </div>

              {shown.created.length > 0 && (
                <>
                  <p className="small fw-semibold mb-1">
                    {preview ? "Will be added and offered to every customer" : "Added"}
                  </p>
                  <ul className="small mb-3">
                    {shown.created.map((name) => (
                      <li key={name}><code>{name}</code></li>
                    ))}
                  </ul>
                </>
              )}

              {shown.skipped.length > 0 && (
                <>
                  <p className="small fw-semibold mb-1">Skipped</p>
                  <Table size="sm" responsive className="small mb-3">
                    <tbody>
                      {shown.skipped.map((s) => (
                        <tr key={s.name}>
                          <td><code>{s.name}</code></td>
                          <td className="text-muted">{s.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}

              {shown.updated.length > 0 && (
                <p className="small text-muted mb-0">
                  {shown.updated.length} template{shown.updated.length === 1 ? "" : "s"} already
                  here {preview ? "will have" : "had"} their status and wording refreshed
                  from Meta. Customers' own templates stay theirs.
                </p>
              )}
            </Card.Body>
          </Card>
        )}

        <div className="d-flex justify-content-between align-items-center mb-2 gap-3 flex-wrap">
          <ButtonGroup size="sm">
            {[
              ["all", `All (${templates.length})`],
              ["shared", `Shared (${sharedCount})`],
              ["customer", `Customers' own (${templates.length - sharedCount})`],
            ].map(([key, label]) => (
              <Button
                key={key}
                variant={owner === key ? "primary" : "outline-primary"}
                onClick={() => setOwner(key)}
              >
                {label}
              </Button>
            ))}
          </ButtonGroup>
          <Form.Control
            size="sm"
            style={{ maxWidth: 280 }}
            placeholder="Search name, wording or customer"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {loadingList ? (
          <p className="text-muted">Loading…</p>
        ) : (
          <Table responsive className="align-middle small">
            <thead>
              <tr>
                <th>Name</th>
                <th>Message</th>
                <th>Attachment</th>
                <th>Owner</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-muted text-center py-4">
                    {templates.length === 0
                      ? "No templates yet. Press Sync from Meta to bring them in."
                      : "No template matches."}
                  </td>
                </tr>
              )}

              {visible.map((t) => {
                const look = STATUS_LOOK[t.status] || { bg: "secondary", label: t.status };

                return (
                  <tr key={t.id}>
                    <td style={{ minWidth: 180 }}>
                      <div className="fw-semibold">{t.display_name}</div>
                      <code className="text-muted">{t.name}</code>
                    </td>
                    <td className="text-muted" style={{ maxWidth: 420 }}>
                      <div
                        title={t.body_text}
                        style={{
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {t.body_text}
                      </div>
                      {t.placeholder_count > 0 && (
                        <div className="mt-1">
                          {t.placeholder_count} blank{t.placeholder_count === 1 ? "" : "s"} to
                          fill per campaign
                        </div>
                      )}
                    </td>
                    <td>{ATTACHMENT_LABEL[t.header_type] || t.header_type}</td>
                    <td>
                      {t.customer_id ? (
                        t.customer?.organization_name || t.customer?.name || "A customer"
                      ) : (
                        <Badge bg="light" text="dark">Shared</Badge>
                      )}
                    </td>
                    <td>
                      <Badge bg={look.bg}>{look.label}</Badge>
                      {t.requires_button_parameters && (
                        <div className="text-danger mt-1">
                          Hidden from campaigns: has a button that needs a link per guest
                        </div>
                      )}
                      {t.status === "rejected" && t.rejection_reason && (
                        <div className="text-danger mt-1">{t.rejection_reason}</div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </div>
    </CommonLayout>
  );
}
